import { useMemo } from 'react'
import {
  isShapeFrozenForGeometry,
  isShapeLockedForUserEdit,
} from '@/lib/groups/element-edit-policy'
import {
  FROZEN_SELECTION_COLOR,
  HANDLE_BORDER_WIDTH,
  LOCKED_SELECTION_COLOR,
  SELECTION_COLOR,
} from '@/components/app/editor/selection-chrome/selection-chrome-constants'
import { isShapeSelectable } from '../helpers/selection-scope'
import type { ShapeManagerStateApi } from './use-shape-manager-state'
import type { EditScopeRefsApi } from './use-edit-scope-refs'

export function useSelectionPresentation(ctx: ShapeManagerStateApi & EditScopeRefsApi) {
  const { editScopeGroupIdRef, scale, selectedShapeIds, shapes } = ctx

  const shapesById = useMemo(() => new Map(shapes.map((shape) => [shape.id, shape])), [shapes])

  const selectedShapes = useMemo(
    () =>
      shapes.filter(
        (s) => selectedShapeIds.includes(s.id) && isShapeSelectable(s, editScopeGroupIdRef.current)
      ),
    [shapes, selectedShapeIds, editScopeGroupIdRef]
  )
  const selectedShape = selectedShapes[0] ?? null
  const isMultiSelect = selectedShapeIds.length > 1
  const isSingleSelect = selectedShapes.length === 1
  const isSingleLockedSelected =
    isSingleSelect && selectedShape != null && isShapeLockedForUserEdit(selectedShape, shapesById)
  const isSingleFrozenOnlySelected =
    isSingleSelect &&
    selectedShape != null &&
    isShapeFrozenForGeometry(selectedShape, shapesById) &&
    !isShapeLockedForUserEdit(selectedShape, shapesById)
  const isLockedMultiSelected =
    isMultiSelect &&
    selectedShapes.length > 0 &&
    selectedShapes.every((shape) => isShapeLockedForUserEdit(shape, shapesById))
  const isFrozenOnlyMultiSelected =
    isMultiSelect &&
    selectedShapes.length > 0 &&
    !isLockedMultiSelected &&
    selectedShapes.every((shape) => isShapeFrozenForGeometry(shape, shapesById))
  const multiSelectionStrokeColor = isLockedMultiSelected
    ? LOCKED_SELECTION_COLOR
    : isFrozenOnlyMultiSelected
      ? FROZEN_SELECTION_COLOR
      : SELECTION_COLOR
  /**
   * `HANDLE_BORDER_WIDTH` expressed in DOCUMENT px, so the unified-selection outline below holds a
   * constant screen size like every other piece of chrome. That outline is a plain `<div>` border
   * inside the zoom-scaled layer, so without the division it grew with zoom — WMPCM-9868's "text
   * border changes size when zooming", reachable because a text frame is a shape and any mixed
   * selection containing one renders through that path.
   */
  const selectionOutlineDocWidth = HANDLE_BORDER_WIDTH / Math.max(scale, 1e-4)

  return {
    shapesById,
    selectedShapes,
    selectedShape,
    isMultiSelect,
    isSingleSelect,
    isSingleLockedSelected,
    isSingleFrozenOnlySelected,
    isLockedMultiSelected,
    isFrozenOnlyMultiSelected,
    multiSelectionStrokeColor,
    selectionOutlineDocWidth,
  }
}

export type SelectionPresentationApi = ReturnType<typeof useSelectionPresentation>
